import { parseTree, printParseErrorCode, type Node, type ParseError } from "jsonc-parser";
import { LineCounter, parseDocument, stringify } from "yaml";

export type DataFormat = "json" | "yaml";

export interface WorkbenchIssue {
  severity: "error" | "warning";
  code: string;
  message: string;
  line: number;
  column: number;
}

export interface WorkbenchStats {
  bytes: number;
  lines: number;
  depth: number;
  nodes: number;
  rootType: string;
}

export interface WorkbenchResult {
  ok: boolean;
  format: DataFormat;
  outputFormat: DataFormat;
  formattedInput: string;
  convertedOutput: string;
  issues: WorkbenchIssue[];
  stats: WorkbenchStats;
}

export const MAX_INPUT_BYTES = 512 * 1024;
export const MAX_DEPTH = 64;
export const MAX_ALIAS_COUNT = 20;

export const DATA_EXAMPLES: Record<DataFormat, string> = {
  json: `{
  "project": "VPScope",
  "region": "ap-east",
  "services": [
    {
      "name": "web",
      "port": 443,
      "enabled": true
    },
    {
      "name": "metrics",
      "port": 9100,
      "enabled": false
    }
  ]
}`,
  yaml: `project: VPScope
region: ap-east
services:
  - name: web
    port: 443
    enabled: true
  - name: metrics
    port: 9100
    enabled: false
`,
};

function byteLength(source: string): number {
  return new TextEncoder().encode(source).length;
}

function lineCount(source: string): number {
  return source.length === 0 ? 0 : source.split(/\r\n|\r|\n/).length;
}

function positionAt(source: string, offset: number): { line: number; column: number } {
  const before = source.slice(0, Math.max(0, offset));
  const parts = before.split(/\r\n|\r|\n/);
  return { line: parts.length, column: (parts.at(-1)?.length ?? 0) + 1 };
}

function rootType(value: unknown): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value === "object" ? "object" : typeof value;
}

function baseStats(source: string): WorkbenchStats {
  return { bytes: byteLength(source), lines: lineCount(source), depth: 0, nodes: 0, rootType: "-" };
}

function emptyResult(source: string, format: DataFormat, issues: WorkbenchIssue[]): WorkbenchResult {
  return {
    ok: false,
    format,
    outputFormat: format === "json" ? "yaml" : "json",
    formattedInput: "",
    convertedOutput: "",
    issues,
    stats: baseStats(source),
  };
}

function jsonIssue(source: string, error: ParseError): WorkbenchIssue {
  const position = positionAt(source, error.offset);
  const labels: Partial<Record<ReturnType<typeof printParseErrorCode>, string>> = {
    InvalidCommentToken: "标准 JSON 不允许注释。",
    InvalidNumberFormat: "数字格式无效。",
    PropertyNameExpected: "对象属性名必须是双引号字符串。",
    ValueExpected: "此处缺少 JSON 值。",
    ColonExpected: "属性名后缺少冒号。",
    CommaExpected: "值之间缺少逗号，或存在尾随逗号。",
    CloseBraceExpected: "对象缺少右花括号。",
    CloseBracketExpected: "数组缺少右方括号。",
    EndOfFileExpected: "仅支持一个 JSON 根值。",
  };
  const code = printParseErrorCode(error.error);
  return {
    severity: "error",
    code,
    message: labels[code] ?? `JSON 语法错误：${code}。`,
    ...position,
  };
}

interface JsonWalkState {
  issues: WorkbenchIssue[];
  nodes: number;
  depth: number;
}

function walkJson(node: Node, source: string, depth: number, state: JsonWalkState): void {
  state.nodes += 1;
  state.depth = Math.max(state.depth, depth);
  if (depth > MAX_DEPTH && !state.issues.some((issue) => issue.code === "MAX_DEPTH")) {
    state.issues.push({ severity: "error", code: "MAX_DEPTH", message: `文档深度超过 ${MAX_DEPTH} 层上限。`, ...positionAt(source, node.offset) });
  }

  if (node.type === "object") {
    const names = new Map<string, number>();
    for (const property of node.children ?? []) {
      const key = property.children?.[0];
      const value = property.children?.[1];
      if (!key || !value) continue;
      const name = String(key.value);
      if (names.has(name)) {
        state.issues.push({ severity: "error", code: "DUPLICATE_KEY", message: `重复属性名“${name}”会产生不确定结果。`, ...positionAt(source, key.offset) });
      } else {
        names.set(name, key.offset);
      }
      walkJson(value, source, depth + 1, state);
    }
    return;
  }

  if (node.type === "array") {
    for (const child of node.children ?? []) walkJson(child, source, depth + 1, state);
    return;
  }

  if (node.type === "number" && (!Number.isFinite(node.value) || (Number.isInteger(node.value) && !Number.isSafeInteger(node.value)))) {
    state.issues.push({
      severity: "error",
      code: "UNSAFE_NUMBER",
      message: "该整数超出 JavaScript 可安全保真的范围（±9,007,199,254,740,991）。",
      ...positionAt(source, node.offset),
    });
  }
}

function jsonNodeValue(node: Node): unknown {
  if (node.type === "object") {
    const value = Object.create(null) as Record<string, unknown>;
    for (const property of node.children ?? []) {
      const key = property.children?.[0];
      const child = property.children?.[1];
      if (!key || !child) continue;
      Object.defineProperty(value, String(key.value), {
        configurable: true,
        enumerable: true,
        writable: true,
        value: jsonNodeValue(child),
      });
    }
    return value;
  }
  if (node.type === "array") return (node.children ?? []).map(jsonNodeValue);
  return node.value ?? null;
}

interface SafeWalkState {
  nodes: number;
  depth: number;
}

function toJsonSafe(value: unknown, depth: number, ancestors: Set<object>, state: SafeWalkState): unknown {
  state.nodes += 1;
  state.depth = Math.max(state.depth, depth);
  if (depth > MAX_DEPTH) throw new Error(`文档深度超过 ${MAX_DEPTH} 层上限。`);

  if (typeof value === "bigint") {
    if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
      throw new Error("整数超出 JavaScript 可安全保真的范围（±9,007,199,254,740,991）。");
    }
    return Number(value);
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) throw new Error("JSON 无法表达 NaN 或无穷大。请改为字符串或有限数字。");
    if (Number.isInteger(value) && !Number.isSafeInteger(value)) throw new Error("整数超出 JavaScript 可安全保真的范围（±9,007,199,254,740,991）。");
    return value;
  }
  if (value === null || typeof value === "string" || typeof value === "boolean") return value;
  if (typeof value !== "object") throw new Error(`JSON 无法表达 ${typeof value} 类型。`);
  if (ancestors.has(value)) throw new Error("文档包含循环别名，无法转换为 JSON。");

  ancestors.add(value);
  try {
    if (Array.isArray(value)) return value.map((item) => toJsonSafe(item, depth + 1, ancestors, state));
    if (value instanceof Map) {
      const output = Object.create(null) as Record<string, unknown>;
      for (const [key, item] of value) {
        if (typeof key !== "string") throw new Error("JSON 对象只支持字符串属性名。");
        Object.defineProperty(output, key, {
          configurable: true,
          enumerable: true,
          writable: true,
          value: toJsonSafe(item, depth + 1, ancestors, state),
        });
      }
      return output;
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) throw new Error("文档包含 JSON 无法表达的对象类型。");
    const output = Object.create(null) as Record<string, unknown>;
    for (const [key, item] of Object.entries(value)) {
      Object.defineProperty(output, key, {
        configurable: true,
        enumerable: true,
        writable: true,
        value: toJsonSafe(item, depth + 1, ancestors, state),
      });
    }
    return output;
  } finally {
    ancestors.delete(value);
  }
}

function parseJson(source: string, indent: 2 | 4): WorkbenchResult {
  const errors: ParseError[] = [];
  const tree = parseTree(source, errors, { allowEmptyContent: false, allowTrailingComma: false, disallowComments: true });
  if (!tree || errors.length > 0) return emptyResult(source, "json", errors.map((error) => jsonIssue(source, error)));

  const walk: JsonWalkState = { issues: [], nodes: 0, depth: 0 };
  walkJson(tree, source, 1, walk);
  if (walk.issues.length > 0) return { ...emptyResult(source, "json", walk.issues), stats: { ...baseStats(source), nodes: walk.nodes, depth: walk.depth, rootType: tree.type } };

  const value = jsonNodeValue(tree);
  return {
    ok: true,
    format: "json",
    outputFormat: "yaml",
    formattedInput: `${JSON.stringify(value, null, indent)}\n`,
    convertedOutput: stringify(value, { indent, lineWidth: 0, schema: "core", version: "1.2" }),
    issues: [],
    stats: { ...baseStats(source), nodes: walk.nodes, depth: walk.depth, rootType: rootType(value) },
  };
}

function parseYaml(source: string, indent: 2 | 4): WorkbenchResult {
  const lineCounter = new LineCounter();
  const document = parseDocument(source, {
    intAsBigInt: true,
    lineCounter,
    logLevel: "silent",
    merge: false,
    prettyErrors: true,
    resolveKnownTags: false,
    schema: "core",
    strict: true,
    stringKeys: true,
    uniqueKeys: true,
    version: "1.2",
  });
  const yamlMessages: Record<string, string> = {
    DUPLICATE_KEY: "映射键必须唯一，当前键已重复。",
    NON_STRING_KEY: "JSON 对象只支持字符串属性名。",
    TAG_RESOLVE_FAILED: "无法解析此自定义标签；为避免改变语义，工作台不会转换。",
    MULTIPLE_DOCS: "一次只能处理一个 YAML 文档。",
    BAD_INDENT: "缩进层级无效。",
    TAB_AS_INDENT: "YAML 缩进不能使用制表符。",
    MISSING_CHAR: "文档缺少必要的分隔字符。",
    UNEXPECTED_TOKEN: "文档中存在意外的语法标记。",
    BAD_ALIAS: "别名没有对应的有效锚点。",
    RESOURCE_EXHAUSTION: "文档结构超过安全解析上限。",
  };
  const yamlIssues = [...document.errors, ...document.warnings].map((error): WorkbenchIssue => {
    const position = error.linePos?.[0] ?? lineCounter.linePos(error.pos[0]);
    return {
      severity: "error",
      code: error.code,
      message: yamlMessages[error.code] ?? `YAML 语法或结构无效（${error.code}）。`,
      line: position.line,
      column: position.col,
    };
  });
  if (yamlIssues.length > 0) return emptyResult(source, "yaml", yamlIssues);
  if (document.contents === null) {
    return emptyResult(source, "yaml", [{ severity: "error", code: "EMPTY_DOCUMENT", message: "请输入一个 YAML 文档。", line: 1, column: 1 }]);
  }

  try {
    const expanded = document.toJS({ mapAsMap: true, maxAliasCount: MAX_ALIAS_COUNT });
    const walk: SafeWalkState = { nodes: 0, depth: 0 };
    const value = toJsonSafe(expanded, 1, new Set(), walk);
    return {
      ok: true,
      format: "yaml",
      outputFormat: "json",
      formattedInput: document.toString({ indent, lineWidth: 0 }),
      convertedOutput: `${JSON.stringify(value, null, indent)}\n`,
      issues: [],
      stats: { ...baseStats(source), nodes: walk.nodes, depth: walk.depth, rootType: rootType(value) },
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : "YAML 无法安全转换。";
    const code = /alias count|excessive alias/i.test(message) ? "MAX_ALIAS_COUNT" : /深度/.test(message) ? "MAX_DEPTH" : "UNSAFE_VALUE";
    const displayMessage = code === "MAX_ALIAS_COUNT" ? `YAML 别名展开超过 ${MAX_ALIAS_COUNT} 次上限。` : message;
    return emptyResult(source, "yaml", [{ severity: "error", code, message: displayMessage, line: 1, column: 1 }]);
  }
}

export function analyzeData(source: string, format: DataFormat, indent: 2 | 4 = 2): WorkbenchResult {
  const size = byteLength(source);
  if (size > MAX_INPUT_BYTES) {
    return emptyResult(source, format, [{ severity: "error", code: "MAX_INPUT_BYTES", message: "输入超过 512 KiB 本地处理上限。", line: 1, column: 1 }]);
  }
  if (source.trim().length === 0) {
    return emptyResult(source, format, [{ severity: "error", code: "EMPTY_DOCUMENT", message: `请输入 ${format.toUpperCase()} 文档。`, line: 1, column: 1 }]);
  }
  return format === "json" ? parseJson(source, indent) : parseYaml(source, indent);
}
